import React, { useCallback, useEffect, useState } from 'react';

import { format, parseISO } from 'date-fns';

import { Row, Col, Button } from 'react-bootstrap';
import { FaTrashAlt, FaPrint, FaReply } from 'react-icons/fa';

import api from '../../services/api';

import { TIPO_ANEXO } from '../../utils/funcoes';

import { useToast } from '../../hooks/toast';

import { ContainerDados } from './styles';
import Cadastro from './cadastro';
import Modal from '../Modal';

import Loading from '../Loading';

import getResponseErrors from '../../utils/getResponseErrors';

export interface IAnexo {
  AneCodigo: number;
  AneTipo: string;
  AneData: Date;
  AneDataFormatado: string;
  AneNome: string;
  AneArquivo: string;
  arquivo_url: string;
}

interface IReturnAnexo {
  data: IAnexo[];
  count: number;
}

interface IRegistroExcluir {
  codigo: number;
  tipo: string;
}

type PacienteProps = {
  pacCodigo: number;
};

const PacienteDocumento: React.FC<PacienteProps> = ({ pacCodigo }) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showCadastro, setShowCadastro] = useState(false);
  const [tipoSelecionado, setTipoSelecionado] = useState<string>();
  const [docGeral, setDocGeral] = useState<IAnexo[]>();
  const [exame, setExame] = useState<IAnexo[]>();
  const [showModalExcluir, setShowModalExcluir] = useState<IRegistroExcluir>();

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IReturnAnexo>(
          `/anexos/paciente/${pacCodigo}/`,
        );

        const { data } = response.data;

        const dadosFormatados = data.map(rel => {
          return {
            ...rel,
            AneDataFormatado: format(
              parseISO(rel.AneData.toString()),
              'dd/MM/yyyy',
            ),
          };
        });

        setDocGeral(
          dadosFormatados.filter(doc => doc.AneTipo === TIPO_ANEXO.GERAL),
        );
        setExame(
          dadosFormatados.filter(doc => doc.AneTipo === TIPO_ANEXO.EXAME),
        );
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (pacCodigo && pacCodigo > 0) {
      loadDados();
    }
  }, [addToast, pacCodigo]);

  const handleDelete = useCallback(
    async (dados: IRegistroExcluir) => {
      setLoading(true);
      setShowModalExcluir(undefined);
      try {
        const { codigo, tipo } = dados;
        await api.delete(`anexos/${codigo}`);

        if (tipo === TIPO_ANEXO.GERAL) {
          setDocGeral(state => {
            return state?.filter(doc => doc.AneCodigo !== codigo);
          });
        }
        if (tipo === TIPO_ANEXO.EXAME) {
          setExame(state => {
            return state?.filter(doc => doc.AneCodigo !== codigo);
          });
        }
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível excluir o arquivo',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleRegistroIncluido = useCallback(
    (dados: IAnexo) => {
      try {
        const dadosFormatados = {
          ...dados,
          AneDataFormatado: format(
            parseISO(dados.AneData.toString()),
            'dd/MM/yyyy',
          ),
        };

        if (dados.AneTipo === TIPO_ANEXO.GERAL) {
          setDocGeral(state => state?.concat([dadosFormatados]));
        }
        if (dados.AneTipo === TIPO_ANEXO.EXAME) {
          setExame(state => state?.concat([dadosFormatados]));
        }
        setShowCadastro(false);
        setTipoSelecionado(undefined);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível atualizar o arquivo',
          type: 'error',
          description: mensagemErro,
        });
      }
    },
    [addToast],
  );

  return (
    <>
      {loading && <Loading backgroundBlack={false} />}
      {showModalExcluir && (
        <Modal
          handleFecharModal={() => setShowModalExcluir(undefined)}
          show={!!showModalExcluir}
          titulo="Excluir registro"
          large={false}
        >
          <p className="my-3">
            Essa operação não pode ser desfeita, deseja realmente prosseguir?
          </p>
          <div className="d-flex justify-content-between mt-2">
            <Button
              type="button"
              variant="danger"
              onClick={() => handleDelete(showModalExcluir)}
              size="sm"
            >
              <FaTrashAlt /> Sim, excluir
            </Button>

            <Button
              type="submit"
              variant="secondary"
              size="sm"
              onClick={() => setShowModalExcluir(undefined)}
            >
              Não, voltar
            </Button>
          </div>
        </Modal>
      )}
      <ContainerDados className="rounded">
        {showCadastro && tipoSelecionado ? (
          <>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="my-2"
              onClick={() => {
                setShowCadastro(false);
                setTipoSelecionado(undefined);
              }}
            >
              <FaReply size={16} /> Voltar para lista
            </Button>
            <Cadastro
              tipo={tipoSelecionado}
              pacCodigo={pacCodigo}
              handleDadosSalvos={handleRegistroIncluido}
            />
          </>
        ) : (
          <Row>
            <Col md={6}>
              <div className="d-flex justify-content-between aling-items-center my-2">
                <h5>Exames</h5>
                <Button
                  type="button"
                  onClick={() => {
                    setTipoSelecionado(TIPO_ANEXO.EXAME);
                    setShowCadastro(true);
                  }}
                  variant="primary"
                  size="sm"
                >
                  Adicionar Exame
                </Button>
              </div>
              {exame && exame.length > 0 ? (
                <table
                  className="table table-hover table-sm table-font-small"
                  style={{ borderWidth: '0px' }}
                >
                  <thead>
                    <tr className="bg-light text-dark">
                      <th style={{ width: '85px' }}>Data</th>
                      <th>Descrição</th>
                      <th style={{ width: '45px', textAlign: 'center' }}>-</th>
                      <th style={{ width: '60px', textAlign: 'center' }}>-</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exame.map(pag => (
                      <tr
                        key={`receitas-${pag.AneCodigo}`}
                        style={{
                          fontSize: '.7rem',
                          height: '30px',
                        }}
                      >
                        <td>{pag.AneDataFormatado}</td>
                        <td>{pag.AneNome}</td>
                        <td
                          className="text-center"
                          style={{ position: 'relative' }}
                        >
                          <a
                            href={pag.arquivo_url}
                            rel="noreferrer"
                            target="_blank"
                          >
                            <FaPrint size={16} />
                          </a>
                        </td>
                        <td className="text-center">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            onClick={
                              () =>
                                setShowModalExcluir({
                                  codigo: pag.AneCodigo,
                                  tipo: pag.AneTipo,
                                })
                              // eslint-disable-next-line react/jsx-curly-newline
                            }
                          >
                            <FaTrashAlt size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Nenhum exame adicionado</p>
              )}
            </Col>
            <Col md={6}>
              <div className="d-flex justify-content-between aling-items-center my-2">
                <h5>Anexos</h5>
                <Button
                  type="button"
                  onClick={() => {
                    setTipoSelecionado(TIPO_ANEXO.GERAL);
                    setShowCadastro(true);
                  }}
                  variant="primary"
                  size="sm"
                >
                  Adicionar
                </Button>
              </div>
              {docGeral && docGeral.length > 0 ? (
                <table
                  className="table table-hover table-sm table-font-small"
                  style={{ borderWidth: '0px' }}
                >
                  <thead>
                    <tr className="bg-light text-dark">
                      <th style={{ width: '85px' }}>Data</th>
                      <th>Descricao</th>
                      <th style={{ width: '60px', textAlign: 'center' }}>-</th>
                      <th style={{ width: '60px', textAlign: 'center' }}>-</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docGeral.map(pag => (
                      <tr
                        key={`agenda-${pag.AneCodigo}`}
                        style={{
                          fontSize: '.7rem',
                          height: '30px',
                        }}
                      >
                        <td>{pag.AneDataFormatado}</td>
                        <td>{pag.AneNome}</td>
                        <td className="text-center">
                          <a
                            href={pag.arquivo_url}
                            rel="noreferrer"
                            target="_blank"
                          >
                            <FaPrint size={16} />
                          </a>
                        </td>
                        <td className="text-center">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            onClick={
                              () =>
                                setShowModalExcluir({
                                  codigo: pag.AneCodigo,
                                  tipo: pag.AneTipo,
                                })
                              // eslint-disable-next-line react/jsx-curly-newline
                            }
                          >
                            <FaTrashAlt size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>Nenhum anexo adicionado</p>
              )}
            </Col>
          </Row>
        )}
      </ContainerDados>
    </>
  );
};

export default PacienteDocumento;
